/** @module utils/currency */

// Supported display currencies for the trial checkout pages. The backend selects
// the matching Stripe price and charges in this currency.
export type Currency = "usd" | "cad" | "aud";

// Reads the `currency` query param (default USD).
export const parseCurrency = (value?: string | null): Currency => {
  const currency = value?.toLowerCase();
  return currency === "cad" || currency === "aud" ? currency : "usd";
};

// " CAD" / " AUD" for non-USD; empty for USD (which keeps the bare "$" look).
export const currencySuffix = (currency: Currency): string =>
  currency === "usd" ? "" : ` ${currency.toUpperCase()}`;

// "$900" for whole dollars, "$89.99" when there are cents, "$1,300" once the
// amount reaches a thousand; appends the currency code for non-USD.
//
// The thousands separator is not cosmetic here: the AUD Academy yearly plan is
// 130000 cents, and String(1300) rendered it as "$1300" on the checkout pages
// while every other surface — the offering card via formatServerAmount, and the
// iOS and Android apps — showed "$1,300.00". Pinned to en-US for the same reason
// formatServerAmount is: it has to match what the API and the mobile clients
// produce, not the reviewer's locale.
export const formatPrice = (cents: number, currency: Currency = "usd"): string => {
  const dollars = cents / 100;
  const amount = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  return `$${amount}${currencySuffix(currency)}`;
};

// Same rule as formatPrice, but for amounts that arrive in whole currency units
// rather than cents, and without a symbol, for callers that resolve their own.
// Booking option rates are the case: `lesson_rates.rate_cents` is serialized as
// an amount, so a 50.50 option arrives as 50.5 and must not be rounded away.
export const formatAmount = (amount: number): string =>
  Number.isInteger(amount)
    ? amount.toLocaleString("en-US")
    : amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

// Always two decimals (e.g. "$52.49" / "$52.49 CAD"). Used for the tax-inclusive
// lines, where cents are always shown.
export const formatMoney = (cents: number, currency: Currency = "usd"): string =>
  `$${(cents / 100).toFixed(2)}${currencySuffix(currency)}`;

// CN-2671: the scheduling API sends offering prices already formatted for
// display, so a $1,350 package arrives as the string "1,350.00" — and arrives as
// "1350.00" on the endpoints the CN-2671 stopgap covers, since older mobile
// builds parse the field and choke on the separator. Read either shape.
export const parseServerAmount = (value?: string | number | null): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Render the amount ourselves rather than echoing whatever the API sent, so the
// page keeps its thousands separator whichever shape arrives. Pinned to en-US to
// match the API's own formatting and what the iOS and Android apps show.
export const formatServerAmount = (value?: string | number | null): string =>
  parseServerAmount(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
